import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { CssBaseline, Link, ThemeProvider, useMediaQuery } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";

import Navbar from "./components/NavBar";
import HeroSection from "./components/HeroSection";
import FeaturesSection from "./components/FeaturesSection";
import HowItWorks from "./components/HowItWorks";
import Testimonials from "./components/Testemonials";
import CallToAction from "./components/CallToAction";
import Footer from "./components/Footer";
import { lightTheme, darkTheme } from "./theme";
import Pricing from "./components/Pricing";
import Demo from "./components/Demo";
import FeatureList from "./components/FeatureList";
import FAQ from "./components/FAQ";
import ContactForm from "./components/ContactForm";
import NotFound from "./components/NotFound";
import About from "./components/About";
import Security from "./components/Security";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import Accessibility from "./components/Accessibility";
import Seo from "./components/Seo";
import {
  breadcrumbSchema,
  organizationSchema,
  pricingPageSchema,
  softwareApplicationSchema,
  webSiteSchema,
} from "./seo/structuredData";
import { trackPageView } from "./utils/demoTracking";

const MAIN_CONTENT_ID = "main-content";

/**
 * First thing in the tab order, and invisible until it has focus.
 *
 * Every page opens with the same header, so without this a keyboard or switch user pays for the
 * navigation again on every single route before reaching anything they came for. It is moved
 * off-screen rather than hidden with `display: none` or `visibility: hidden`, either of which would
 * take it out of the tab order and defeat the point.
 */
function SkipLink() {
  return (
    <Link
      href={`#${MAIN_CONTENT_ID}`}
      sx={{
        position: "fixed",
        left: 8,
        // Far enough above the viewport to clear its own height at any font size.
        top: -80,
        zIndex: (theme) => theme.zIndex.tooltip + 1,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        backgroundColor: "background.paper",
        color: "text.primary",
        border: 2,
        borderColor: "primary.main",
        textDecoration: "none",
        "&:focus": { top: 8 },
      }}
    >
      Skip to main content
    </Link>
  );
}

/**
 * Records a page view whenever the route changes.
 *
 * This lives inside the Router because it needs `useLocation`, and it renders nothing: it exists
 * purely so that client-side navigations are counted. Without it only the very first URL of a visit
 * would ever be measured, which for a single-page app means almost no traffic data at all.
 *
 * Scrolls to the top on navigation too: React Router preserves scroll position by default, so moving
 * from halfway down the FAQ to the privacy page otherwise landed the reader in the middle of it.
 *
 * A client-side navigation also replaces the page without the browser doing anything about focus, so
 * a screen reader announces nothing and the tab order stays wherever the old page left it. Moving
 * focus to the new h1 restores what a full page load would have done, and lands it on the heading
 * that says which page this now is.
 */
function RouteChangeEffects() {
  const { pathname } = useLocation();
  // Seeded with the pathname already on screen, not a bare `true`. A one-shot boolean looked
  // equivalent but wasn't: React 18 StrictMode runs a mount effect an extra time in development
  // (mount, cleanup, mount again) without resetting refs, so the flag was already flipped to
  // false by the time the "real" mount effect ran, and the very first paint stole its own focus
  // onto the heading. Comparing pathnames survives that extra run because both invocations see
  // the same pathname and neither looks like a navigation; a genuine route change is the only
  // thing that changes the value being compared against.
  const previousPathname = React.useRef(pathname);

  React.useEffect(() => {
    trackPageView(pathname);
    window.scrollTo(0, 0);

    // Not on first paint: focus is the browser's to place on a fresh load, and stealing it there
    // would fight the fragment in a deep link.
    if (pathname === previousPathname.current) {
      return;
    }
    previousPathname.current = pathname;

    const heading = document.querySelector(`#${MAIN_CONTENT_ID} h1`);
    if (heading) {
      // Headings are not focusable by default. -1 makes this one a focus target without adding it to
      // the tab order, so nobody tabbing through the page stops on it.
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
  }, [pathname]);

  return null;
}

const App = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  return (
    <HelmetProvider>
      <ThemeProvider theme={prefersDarkMode ? darkTheme : lightTheme}>
        {/* Applies the theme's background and text colours to <body>, which is what stops the page
            from showing a white surround in dark mode. */}
        <CssBaseline />
        <Router>
          <RouteChangeEffects />
          <SkipLink />
          <Navbar />
          {/* The old `calc(100vh - 64px - 80px)` hard-coded a 64px toolbar and an 80px footer. The
              toolbar is 56px on mobile, and the footer is now considerably taller than 80px, so the
              subtraction was wrong on every viewport, leaving short pages with a stretch of empty
              space below the footer. Letting the content size itself and pushing the footer down with
              flex gets the same result without asserting anyone's height. */}
          <main
            id={MAIN_CONTENT_ID}
            // Focusable only as a target: following the skip link moves the caret here rather than
            // just scrolling the viewport and leaving focus back in the header.
            tabIndex={-1}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: "1 0 auto",
            }}
          >
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Seo
                      route="/"
                      jsonLd={[
                        organizationSchema,
                        webSiteSchema,
                        softwareApplicationSchema,
                      ]}
                    />
                    <HeroSection />
                    <FeaturesSection />
                    <HowItWorks />
                    <Testimonials />
                    <CallToAction />
                  </>
                }
              />
              <Route
                path="/pricing"
                element={
                  <>
                    <Seo
                      route="/pricing"
                      jsonLd={[
                        pricingPageSchema,
                        softwareApplicationSchema,
                        breadcrumbSchema("Pricing", "/pricing"),
                      ]}
                    />
                    <Pricing />
                    <CallToAction />
                  </>
                }
              />
              <Route
                path="/contact"
                element={
                  <>
                    <Seo
                      route="/contact"
                      jsonLd={breadcrumbSchema("Contact", "/contact")}
                    />
                    <ContactForm />
                  </>
                }
              />
              <Route
                path="/faqs"
                element={
                  <>
                    <FAQ />
                    <CallToAction />
                  </>
                }
              />
              <Route
                path="/demo"
                element={
                  <>
                    <Seo
                      route="/demo"
                      jsonLd={breadcrumbSchema("Demo", "/demo")}
                    />
                    <Demo />
                    <FeatureList />
                    <CallToAction />
                  </>
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/security" element={<Security />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
